import { useMemo, useContext, useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import { makeStyles, useMediaQuery, Button, Typography, Table, TableHead, TableBody, TableRow, TableCell, Divider } from '@ellucian/react-design-system/core';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { CONSTANTS } from '../Helpers/Configuration';
import {
    colorTextAlertError,
    colorTextNeutral200,
    colorTextNeutral600,
    spacing20,
    spacing30
} from '@ellucian/react-design-system/core/styles/tokens';
import get from 'lodash/get';
import { getRecordNotFoundAttributeForTable } from '../Helpers/ApplicationFilteredSearchHelpers';
import { formatPhoneNumber } from '../Helpers/PhoneHelper';
import { createSelector } from '@reduxjs/toolkit';
import { batch, useDispatch, useSelector } from 'react-redux';
import GlobalLocaleContext from '../GlobalLocale/GlobalLocaleContext';
import useApplicationForm from './useApplicationForm';
import { setError, setForceShowErrors } from '@nsr/react-form';
import { selectCurriculumValues } from './redux/slices/applicationSlice';
import { extractFileSize, findFileMetadata } from '../../utilities/fileUploadService';
import { stripDocumentIdFromName } from '../../shared/fileNameUtils';
import { Icon } from '@ellucian/ds-icons/lib';
const useStyles = makeStyles({
    name: 'QuestionResponse'
})(() => ({
    error: {
        color: colorTextAlertError },
    label: {
        textTransform: 'uppercase !important' },
    value: {
        whiteSpace: 'pre-wrap' },
    root: {
        display: 'flex',
        flexDirection: 'column' },
    tableExpandableRow: {
        padding: ['0px', '!important'],
        margin: ['0px', '!important']
    },
    tableContainer: {
        background: colorTextNeutral200,
        padding: spacing30
    },
    tableCellNoBorder: {
        border: 'none'
    },
    showButton: {
        marginLeft: spacing30
    },
    uploadFileList: {
        marginTop: spacing20,
        display: 'flex',
        flexDirection: 'column',
        gap: spacing20
    },
    uploadFileItem: {
        display: 'flex',
        flexDirection: 'column'
    },
    uploadFileRow: {
        display: 'flex',
        alignItems: 'center'
    },
    uploadFileText: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: spacing30
    },
    uploadFileIcon: {
        display: 'inline-flex',
        marginRight: spacing20,
        color: colorTextNeutral600,
        lineHeight: 1.5
    },
    uploadFileName: {
        fontWeight: 600
    },
    uploadFileSize: {
        color: colorTextNeutral600
    },
    uploadFileGroupDivider: {
        margin: `0 0 ${spacing20} 0`,
        color: colorTextNeutral200
    }
}));

const selectApplicationId = state => state.application?.id;

const QuestionResponse = props => {
    const intl = useIntl();
    const { question, prefix, value, piiHidden, isConditionallyRequired } = props;
    const { classes, cx } = useStyles();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const noResponse = intl.formatMessage({ id: 'noResponseEnteredTextId' });
    const { globalLocale } = useContext(GlobalLocaleContext);
    const { adminTenant, previewMode, adminApi } = useApplicationForm();
    const tenant = window.PRELOADED_STATE?.globalSettings || adminTenant;
    const applicationId = useSelector(selectApplicationId);
    const appSupplementalItems = useSelector(state => state.application?.supplementalItems || {});
    const appSupplementalItemsMap = useSelector(state => state.application?.supplementalItemsMap || {});
    const rawDateFormat = tenant?.settingMessages?.languages[globalLocale]?.dateFormat || CONSTANTS.defaultDateFormat;
    const formatIndex = CONSTANTS.dateFormats.findIndex(f => f.toLowerCase() === rawDateFormat.toLowerCase());
    const dateFormatDisplay = formatIndex >= 0 ? CONSTANTS.dateFormatsDisplay[formatIndex] : CONSTANTS.defaultDateFormatDisplay; // uppercase — for user display
    const [showMaskedValue, setShowMaskedValue] = useState(false);
    const questionPath = useMemo(() => `${prefix}.${question.id}`, [prefix, question]);
    const pageId = useMemo(() => {
        const firstDotIndex = prefix.indexOf('.');

        return firstDotIndex >= 0 ? prefix.substring(0, firstDotIndex) : prefix;
    }, [prefix]);
    const repetition = useMemo(() => {
        // If we are in a repeating section,
        // The repetition index is the third part of the prefix.
        let rep = undefined;
        const parts = prefix?.split?.call(prefix, '.');

        if (parts?.length === 3) {
            try {
                rep = parseInt(parts[2]);
            } catch {
                // Assume no repetition.
            }
        }

        return rep;
    }, [prefix]);
    const selectQuestionErrors = useMemo(() => {
        return createSelector(
            state => get(state.form.errors, questionPath, false),
            value => !!value
        );
    }, [questionPath]);
    const hasErrors = useSelector(selectQuestionErrors);
    const selectForceShowErrors = useMemo(
        () => createSelector(
            state => state.form.meta.forceShowErrors[pageId],
            value => value
        ),
        [pageId]
    );
    const forceShowErrors = useSelector(selectForceShowErrors);
    const isMajorDescription = question.id === CONSTANTS.majorDescriptionSelfServiceQuestionId;
    const curriculumValues = useSelector(selectCurriculumValues);

    const getRegexCustomErrorMessage = () => {
        if (!question?.showRegexCustomError) {
            return '';
        }

        if (question?.regexCustomErrorMessageId) {
            const localizedMessage = intl.formatMessage({
                id: question?.regexCustomErrorMessageId,
                defaultMessage: ''
            });
            if (localizedMessage) {
                return localizedMessage;
            }
        }

        return question?.regexCustomErrorMessage || '';
    };

    const rootClasses = useMemo(() => {
        return cx(classes.root, {
            [classes.error]: hasErrors });
    }, [classes, hasErrors]);

    const labelClasses = useMemo(() => {
        return cx(classes.label, {
            [classes.error]: hasErrors });
    }, [classes, hasErrors]);

    const isValidRegex = useMemo(() => {
        try {
            // Try/Catch regex to determine if it is valid. Use this knowledge when presenting errors in UI
            // If an admin is viewing the page then alert them to the regex issue
            // If a student is viewing the page then hide any issues from them
            new RegExp(question.regexValidationExpression);
            return true;
        }
        catch {
            return false;
        }
    }, [question.regexValidationExpression]);

    useEffect(() => {
        const valueMissing = (!value) || (Array.isArray(value) && value.length === 0);
        if ((question.required || isConditionallyRequired) && valueMissing) {
            batch(() => {
                dispatch(setError({
                    name: questionPath,
                    value: intl.formatMessage({ id: 'validateRequired' })
                }));
                if (!forceShowErrors) {
                    dispatch(setForceShowErrors({
                        name: pageId,
                        value: true
                    }));
                }
            });
        }
        if (question.showRegexValidation && question.regexValidationExpression && value) {
            let isValid = true;
            let errorMsgId = 'invalidRegexValue';
            let customError = '';

            if (piiHidden && question.inputType === 'password') {
                isValid = value !== CONSTANTS.piiMaskedValueRegexError;
            } else if (!isValidRegex) {
                // If the regex is invalid (edge case) then only dispatch an error if it is an admin viewing the value
                // If this is a student viewing this then nothing to see here, pass it.
                errorMsgId = 'invalidRegexExpression';
                isValid = !previewMode;
            } else {
                var regularExpressionValidation = new RegExp(question.regexValidationExpression);
                isValid = regularExpressionValidation.test(value);
                if (!isValid) {
                    customError = getRegexCustomErrorMessage();
                }
            }
            if (!isValid) {
                dispatch(setError({
                    name: questionPath,
                    value: customError || intl.formatMessage({ id: errorMsgId })
                }));
                if (!forceShowErrors) {
                    dispatch(setForceShowErrors({
                        name: pageId,
                        value: true
                    }));
                }
            }
        }
    }, [previewMode, isValidRegex, question.showRegexValidation, question.regexValidationExpression, question.showRegexCustomError, question.regexCustomErrorMessageId, question.regexCustomErrorMessage, question.inputType, piiHidden, forceShowErrors, questionPath, question.required, isConditionallyRequired, value]);

    const buildATypographyRenderValue = (text) => {
        return (
            <Typography variant="body1">{text}</Typography>
        );
    };

    const buildShowMaskedValue = (value, maskedValue) => {
        return (
            <div>
                <Typography variant="body1" display="inline">{showMaskedValue ? value : maskedValue}</Typography>
                <Button className={classes.showButton} variant="text" onClick={() => {
                    const newShowMaskedValue = !showMaskedValue;
                    setShowMaskedValue(newShowMaskedValue);

                    if (adminApi && !previewMode && applicationId) {
                        if (newShowMaskedValue) {
                            adminApi.applicationForms.unmaskPii(applicationId, label, repetition);
                        } else {
                            adminApi.applicationForms.maskPii(applicationId, label, repetition);
                        }
                    }
                }}>
                    {intl.formatMessage({ id: showMaskedValue ? 'hide' : 'show' })}
                </Button>
            </div>
        );
    };

    const getValueFromRecord = (record, field) => {
        if (!field.path || field.path.length === 0) {
            return record[field.value] || record.label || '';
        }
        const pathValues = field.path.map(p => p.value);
        const keyName = `${pathValues.join('.')}.${field.value}`;
        return record[keyName] || record.label || '';
    };

    const buildFilteredSearchRenderValue = (filteredSearch, value) => {
        if (!value || !value?.id) {
            return buildATypographyRenderValue(noResponse);
        }

        let recordValue = value;
        if (value.id.includes('_noRecordFound')) {
            recordValue = getRecordNotFoundAttributeForTable(filteredSearch.recordNotFoundAttribute, intl.formatMessage);
        }
        const displayFields = filteredSearch?.displayAttributes ?? [];
        if (!displayFields || displayFields.length === 0 || !recordValue) {
            return buildATypographyRenderValue(noResponse);
        }

        const noRecordFoundColSpan = (Object.keys(recordValue)?.length - 1) !== displayFields?.length ? displayFields.length : 1;
        const tableHeadsLabels = displayFields.map((field) => intl.formatMessage({ id: field.displayLabelId }));
        return (
            <div className={classes.tableContainer}>
                <Table
                    layout={{
                        variant: 'card',
                        breakpoint: isMobile ? 'lg' : 'md' }}
                    size={'small'}
                >
                    <TableHead>
                        <TableRow>
                            {tableHeadsLabels?.map((label, labelIndex) => {
                                return (
                                    <TableCell align="left" key={`TableHead_TableCell_${labelIndex}`}>
                                        {label}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow className={classes.tableExpandableRow}>
                            {Object.keys(recordValue)?.slice(1)?.length === 1 && recordValue.label && (// Record not found
                                (<TableCell
                                    colSpan={noRecordFoundColSpan}
                                    align={'left'}
                                    columnName={tableHeadsLabels[0]}
                                    className={classes.tableCellNoBorder}
                                >
                                    {recordValue.label || ''}
                                </TableCell>)
                            )}
                            {(Object.keys(recordValue)?.slice(1)?.length > 1 || !recordValue.label) &&
                                displayFields?.map((field, fieldIndex) => {
                                    return (
                                        <TableCell
                                            key={`TableBody_TableCell_${field.value}_${fieldIndex}`}
                                            align={'left'}
                                            columnName={tableHeadsLabels[fieldIndex]}
                                            className={classes.tableCellNoBorder}
                                        >
                                            {getValueFromRecord(recordValue, field)}
                                        </TableCell>
                                    );
                                })}
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        );
    };

    const formatUploadFileSize = (sizeInBytes) => {
        if (!sizeInBytes && sizeInBytes !== 0) {
            return '';
        }

        const kilobyte = 1024;
        const megabyte = kilobyte * 1024;

        if (sizeInBytes >= megabyte) {
            return `${Math.round(sizeInBytes / megabyte)}MB`;
        }

        if (sizeInBytes >= kilobyte) {
            return `${Math.round(sizeInBytes / kilobyte)}KB`;
        }

        return `${sizeInBytes}B`;
    };

    const buildUploadRenderValue = (files) => {
        if (!Array.isArray(files) || files.length === 0) {
            return buildATypographyRenderValue(noResponse);
        }

        return (
            <div className={classes.uploadFileList}>
                {files.map((file, idx) => (
                    <div key={file.id || idx} className={classes.uploadFileItem}>
                        <div className={classes.uploadFileRow}>
                            <span className={classes.uploadFileIcon} aria-hidden="true">
                                <Icon name="file-text" />
                            </span>
                            <Typography variant="body2" className={classes.uploadFileText}>
                                <span className={classes.uploadFileName}>{file.name}</span>
                                {file.size ? (
                                    <span className={classes.uploadFileSize}>
                                        {` (${formatUploadFileSize(file.size)})`}
                                    </span>
                                ) : null}
                            </Typography>
                        </div>
                    </div>
                ))}
                <Divider className={classes.uploadFileGroupDivider} />
            </div>
        );
    };

    const renderValue = useMemo(() => {
        // For upload questions, skip the empty-value guard — supplementalItems may have files
        // even when form.values has no entry (e.g. post-submission uploads)
        if (question.type !== CONSTANTS.controlTypes.upload) {
            if (value === null || value === undefined || value === '') {
                return buildATypographyRenderValue(noResponse);
            }
        }
        if (question.showRegexValidation && question.regexValidationExpression && value !== CONSTANTS.piimaskedValue) {
            if (question.inputType === 'password' && value === CONSTANTS.piiMaskedValueRegexError) {
                return buildATypographyRenderValue(intl.formatMessage({ id: 'invalidRegexValue' }));
            }
            else {
                if (isValidRegex)
                {
                    var regularExpressionValidation = new RegExp(question.regexValidationExpression);
                    if (!regularExpressionValidation.test(value)) {
                        const customRegexErrorMessage = getRegexCustomErrorMessage();
                        if (customRegexErrorMessage) {
                            return buildATypographyRenderValue(customRegexErrorMessage);
                        }
                        return buildATypographyRenderValue(intl.formatMessage({ id: 'invalidRegexValue' }));
                    }
                }
                else if (previewMode)
                {
                    //Only render field with invalid expression if an admin is in preview
                    //Otherwise let code continue to render the value as is since it passes the broken regex for students
                    return buildATypographyRenderValue(intl.formatMessage({ id: 'invalidRegexExpression' }));
                }
            }
        }
        let output = value;
        let found, range, from, to;
        let newOutput = [];

        switch (question.type) {
            case CONSTANTS.controlTypes.upload: {
                const questionId = question.id;
                const supplementalEntry = appSupplementalItems?.[questionId];
                let filesToDisplay = [];
                if (supplementalEntry?.files && Array.isArray(supplementalEntry.files)) {
                    // Use supplementalItems files, filtering out deleted ones
                    filesToDisplay = supplementalEntry.files
                        .filter(file => file?.status !== 'deleted')
                        .map(file => {
                            let size = 0;
                            if (typeof file.size === 'number' && file.size > 0) {
                                size = Number(file.size);
                            } else {
                                size = extractFileSize(file) || 0;
                            }
                            const docId = file.documentId || file.id;
                            const rawName = file.fileName || file.name || file.originalName || String(docId);
                            const displayName = stripDocumentIdFromName(rawName, docId) || rawName;
                            return { id: docId, name: displayName, size };
                        });
                } else if (Array.isArray(output) && output.length > 0) {
                    // Fallback to form.values if no supplementalItems entry exists
                    const displayFiles = output.map(item => {
                        // Handle string documentId
                        if (typeof item === 'string') {
                            // Use findFileMetadata utility for consistent lookup
                            const foundMeta = findFileMetadata(item, appSupplementalItems, appSupplementalItemsMap);
                            // Skip files marked as deleted
                            if (foundMeta && foundMeta.status === 'deleted') {
                                return null;
                            }
                            
                            if (foundMeta) {
                                // Extract size from metadata
                                let size = 0;
                                if (typeof foundMeta.size === 'number' && foundMeta.size > 0) {
                                    size = Number(foundMeta.size);
                                } else {
                                    size = extractFileSize(foundMeta) || 0;
                                }

                                // Get raw name from metadata (fileName is what API stores)
                                const rawName = foundMeta.fileName
                                    || foundMeta.name
                                    || foundMeta.originalName
                                    || foundMeta.displayName
                                    || foundMeta.documentId
                                    || foundMeta.edmFileId
                                    || String(item);

                                // Strip documentId suffix from name for clean display
                                const docId = foundMeta.documentId || foundMeta.id || item;
                                const displayName = stripDocumentIdFromName(rawName, docId) || rawName;

                                return { id: docId, name: displayName, size };
                            }
                            
                            // Fallback: strip any UUID suffix from the documentId string itself
                            const displayName = stripDocumentIdFromName(String(item), item) || String(item);
                            return { id: item, name: displayName, size: 0 };
                        }
                        
                        // Handle object item (already has metadata)
                        if (item && typeof item === 'object') {
                            // Skip files marked as deleted
                            if (item.status === 'deleted') {
                                return null;
                            }
                            
                            let size = 0;
                            if (typeof item.size === 'number' && item.size > 0) {
                                size = item.size;
                            } else {
                                size = extractFileSize(item) || 0;
                            }
                            const docId = item.documentId || item.id;
                            // Check fileName first (API standard), then name, then fall back to id
                            const rawName = item.fileName || (item.name != null ? String(item.name) : String(item.id || ''));
                            // Strip documentId suffix from name for clean display
                            const displayName = stripDocumentIdFromName(rawName, docId) || rawName;
                            return {
                                id: docId || JSON.stringify(item),
                                name: displayName,
                                size };
                        }
                        
                        // Fallback for unexpected types
                        return { id: String(item), name: String(item), size: 0 };
                    }).filter(Boolean);  // Remove null values (deleted files)
                    
                    filesToDisplay = displayFiles;
                }
                // Return rendered output
                if (filesToDisplay && filesToDisplay.length > 0) {
                    return buildUploadRenderValue(filesToDisplay);
                } else {
                    return buildATypographyRenderValue(noResponse);
                }
            }
            case CONSTANTS.controlTypes.datepicker:
                output = moment(value).format(dateFormatDisplay);
                break;
            case CONSTANTS.controlTypes.daterange:
                range = value.split(',');
                [from, to] = range;
                if (range.length === 1 || (!from && !to)) {
                    output = noResponse;
                    break;
                }
                output = intl.formatMessage({ id: 'dateRangeQuestionResponse' }, {
                    from: from ? moment(from).format(dateFormatDisplay) : intl.formatMessage({ id: 'notSpecified' }),
                    to: to ? moment(to).format(dateFormatDisplay) : intl.formatMessage({ id: 'notSpecified' }) });
                break;
            case CONSTANTS.controlTypes.searchandselect:
            case CONSTANTS.controlTypes.checkbox:
                if (Array.isArray(output) && output.length > 0) {
                    output.forEach(item => {
                        found = question.possibleValues.values.find(it => it.value === item);
                        if (found) {
                            if (question.possibleValues.type === CONSTANTS.questionPossibleValueTypes.dynamic) {
                                found.label && newOutput.push(found.label);
                            }
                            else if (found.labelId) {
                                newOutput.push(intl.formatMessage({ id: found.labelId }));
                            }
                            else {
                                newOutput.push(item);
                            }
                        }
                    });
                    output = newOutput.join(', ');
                }
                else {
                    output = noResponse;
                }
                break;
            case CONSTANTS.controlTypes.radiogroup:
                found = question.possibleValues.values.find(it => it.value === value);
                if (found && found.labelId) {
                    output = intl.formatMessage({ id: found.labelId });
                }
                break;
            case CONSTANTS.controlTypes.singlechoice:
                if (isMajorDescription) {
                    found = curriculumValues?.majorDescriptions?.find(it => it.value === value);
                } else {
                    found = question.possibleValues.values.find(it => it.value === value);
                }

                if (found) {
                    if (isMajorDescription ||
                        question.possibleValues.type === CONSTANTS.questionPossibleValueTypes.dynamic
                    ) {
                        output = found.label || '';
                    }
                    else if (found.labelId) {
                        output = intl.formatMessage({ id: found.labelId });
                    }
                }
                break;
            case CONSTANTS.controlTypes.text:
            case CONSTANTS.controlTypes.textarea:
                if (output && question.inputType === 'password') {
                    output = [...output].map(() => '*').join('');
                    if (!piiHidden) {
                        return buildShowMaskedValue(value, output);
                    }
                }
                break;
            case CONSTANTS.controlTypes.filteredsearch:
                return buildFilteredSearchRenderValue(question.filteredSearch, question.possibleValues?.values?.find(value => value.id === output));
            case CONSTANTS.controlTypes.phone:
                output = formatPhoneNumber(output);
                break;
            default:
                break;
        }
        return buildATypographyRenderValue(output);
    }, [question, intl, value, showMaskedValue, previewMode, isValidRegex, curriculumValues, isMajorDescription, classes, appSupplementalItems, appSupplementalItemsMap, noResponse, dateFormatDisplay, piiHidden]);

    const label = useMemo(() => {
        if (question.labelId) {
            return intl.formatMessage({ id: question.labelId });
        }
        return '';
    }, [intl, question]);

    return (
        <div className={rootClasses}>
            {hasErrors && <Typography className={labelClasses} variant="body3">{intl.formatMessage({ id: 'validateRequiredLabel'})}</Typography>}
            <Typography className={labelClasses} variant="body3">{label}</Typography>
            <div className={classes.value}>{renderValue}</div>
        </div>
    );
};
export default QuestionResponse;
